import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  Link,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { useNavigate } from "react-router-dom";

import LinkToolTip from "../common/components/LinkToolTip";
import { KeyValue } from "../interfaces";

const PREFIX = "IntermediaryDialog";

const classes = {
  tooltipPopper: `${PREFIX}-tooltipPopper`,
  condensedTooltip: `${PREFIX}-condensedTooltip`,
};

const StyledLinkToolTip = styled(LinkToolTip)({
  [`& .${classes.tooltipPopper}`]: {
    maxWidth: "100%",
  },
  [`& .${classes.condensedTooltip}`]: {
    maxWidth: 300,
  },
});

interface Props {
  modalHeader: string;
  content: string | React.ReactElement;
  link: string;
  label: string;
  bankName?: string;
  hoverText?: string;
  sameWindow?: boolean;
  condensedTooltip?: boolean;
  button?: boolean;
  closeRedirect?: string;
  displayModal?: boolean;
  buttonProps?: any;
  bold?: boolean;
  styles?: KeyValue;
}

export default function IntermediaryDialog({
  modalHeader,
  content,
  label,
  link,
  bankName,
  hoverText,
  sameWindow,
  button,
  closeRedirect,
  buttonProps,
  bold,
  styles,
}: Props) {
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = (event: any) => {
    event.preventDefault();
    setOpen(true);
  };
  const navigate = useNavigate();

  const handleClose = () => {
    closeRedirect ? navigate(closeRedirect) : setOpen(false);
  };

  const dialog = (
    <Dialog
      open={closeRedirect ? true : open}
      onClose={!closeRedirect ? handleClose : undefined}
      aria-labelledby="intermediary-dialog-modalHeader"
      aria-describedby="intermediary-dialog-description"
      sx={{
        '& div[role="dialog"]': {
          "padding": 6,
          "maxWidth": "800px",
          "& p, & div": {
            color: "#333",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          fontSize: "33px",
          color: "#333",
        }}
        id="intermediary-dialog-modalHeader"
      >
        {modalHeader}
      </DialogTitle>
      <DialogContent>
        <DialogContentText component="div" id="intermediary-dialog-description">
          {content}
        </DialogContentText>
      </DialogContent>
      <Box
        sx={{
          margin: "auto",
          display: "flex",
          gap: 5,
        }}
      >
        <Button
          id="cancel-navigation"
          sx={{
            width: 160,
            height: 40,
          }}
          variant="contained"
          color="secondary"
          onClick={handleClose}
        >
          Go Back
        </Button>
        <Button
          disableElevation
          id="continue-navigation"
          sx={{
            "width": 160,
            "height": 40,
            "&:hover, &:focus": {
              color: "#ffffff",
              borderColor: "#122f59",
              backgroundColor: "#122f59",
            },
          }}
          variant="contained"
          color="primary"
          href={link}
          rel="noopener"
          target={sameWindow ? "_self" : "_blank"}
          onClick={!closeRedirect ? handleClose : undefined}
        >
          Continue
        </Button>
      </Box>
    </Dialog>
  );
  return closeRedirect ? (
    dialog
  ) : (
    <Typography
      sx={{
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        ...styles,
      }}
    >
      <StyledLinkToolTip
        disableHoverListener={!hoverText}
        title={hoverText ? hoverText : ""}
        enterDelay={250}
        TransitionComponent={Fade}
        classes={{
          tooltip: classes.condensedTooltip
            ? classes.condensedTooltip
            : classes.tooltipPopper,
        }}
      >
        {button ? (
          <Button
            aria-label={
              bankName ? `Visit ${bankName} at ${label}.` : `${label}.`
            }
            variant={buttonProps ? buttonProps.variant : "contained"}
            color={buttonProps ? buttonProps.color : "secondary"}
            fullWidth
            onClick={handleClickOpen}
            sx={(theme) => ({
              fontSize: {
                xs: 10,
                sm: theme.typography.button.fontSize,
              },
            })}
          >
            {label}
          </Button>
        ) : (
          <Link
            href="#"
            sx={{
              ...(bold && { fontWeight: "bold", fontSize: 16 }),
              "textDecoration": "underline",
              "&:focus": {
                border: "none",
              },
            }}
            aria-label={
              bankName ? `Visit ${bankName} at ${label}.` : `${label}.`
            }
            className="modal-link"
            onClick={handleClickOpen}
          >
            {label}
          </Link>
        )}
      </StyledLinkToolTip>
      {dialog}
    </Typography>
  );
}
